import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Paperclip,
  Upload,
  FileText,
  Image as ImageIcon,
  Trash2,
  Loader2,
  Info,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';

const MAX_BYTES = 10 * 1024 * 1024; // 10 MB
const MAX_FILES = 10;
const ACCEPT_EXT = ['jpg', 'jpeg', 'png', 'heic', 'heif', 'webp', 'pdf'];
const ERR_UNSUPPORTED = 'Formato não suportado. Envie imagens (JPG, PNG, HEIC) ou documentos PDF.';
const ERR_TOO_LARGE = 'Arquivo muito grande. Tamanho máximo: 10MB por arquivo.';

const relativeTime = (iso) => {
  if (!iso) return '';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'há instantes';
    if (m < 60) return `há ${m} minuto${m > 1 ? 's' : ''}`;
    const h = Math.floor(m / 60);
    if (h < 24) return `há ${h} hora${h > 1 ? 's' : ''}`;
    const d = Math.floor(h / 24);
    return `há ${d} dia${d > 1 ? 's' : ''}`;
  } catch {
    return '';
  }
};

const truncateName = (name, n = 40) => {
  if (!name || name.length <= n) return name;
  const ext = name.includes('.') ? name.split('.').pop() : '';
  const base = ext ? name.slice(0, -(ext.length + 1)) : name;
  return `${base.slice(0, n - ext.length - 4)}…${ext ? '.' + ext : ''}`;
};

const validateLocal = (file) => {
  const ext = (file.name || '').toLowerCase().split('.').pop();
  if (!ACCEPT_EXT.includes(ext)) return ERR_UNSUPPORTED;
  if (file.size > MAX_BYTES) return ERR_TOO_LARGE;
  return null;
};

export const AttachmentsPanel = ({ consultation, onChange }) => {
  const { isDark } = useTheme();
  const [attachments, setAttachments] = useState(consultation?.attachments || []);
  const [pending, setPending] = useState([]); // [{ id, name, progress, error }]
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setAttachments(consultation?.attachments || []);
  }, [consultation?.attachments]);

  const consultationId = consultation?.id;
  const canUpload = attachments.length < MAX_FILES;
  const accept = useMemo(() => ACCEPT_EXT.map((e) => '.' + e).join(','), []);

  const handleFiles = async (filesList) => {
    const files = Array.from(filesList || []);
    if (!files.length) return;
    // Respect remaining slots client-side
    let remaining = MAX_FILES - attachments.length - pending.filter((p) => !p.error).length;
    for (const file of files) {
      if (remaining <= 0) {
        toast.error('Limite de 10 anexos por consulta atingido.');
        break;
      }
      const localErr = validateLocal(file);
      if (localErr) {
        toast.error(localErr);
        continue;
      }
      remaining -= 1;
      const tmpId = `${file.name}-${Date.now()}-${Math.random()}`;
      setPending((prev) => [...prev, { id: tmpId, name: file.name, progress: 0 }]);
      try {
        const result = await api.uploadAttachment(consultationId, file, (pct) => {
          setPending((prev) => prev.map((p) => (p.id === tmpId ? { ...p, progress: pct } : p)));
        });
        const updated = [
          ...attachments,
          {
            attachment_id: result.attachment_id,
            original_filename: result.original_filename,
            file_type: result.file_type,
            mime_type: result.mime_type,
            file_size_bytes: result.file_size_bytes,
            uploaded_at: result.uploaded_at,
          },
        ];
        setAttachments(updated);
        if (onChange) onChange(updated);
        setPending((prev) => prev.filter((p) => p.id !== tmpId));
        toast.success(`Anexo enviado: ${truncateName(file.name)}`);
      } catch (err) {
        const detail = err?.response?.data?.detail || 'Falha ao enviar o anexo.';
        setPending((prev) => prev.map((p) => (p.id === tmpId ? { ...p, error: detail } : p)));
        toast.error(detail);
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };
  const onDragOver = (e) => e.preventDefault();

  const handleView = async (att) => {
    try {
      const url = await api.fetchAttachmentBlobUrl(consultationId, att.attachment_id);
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) toast.error('Pop-up bloqueado pelo navegador.');
      // Revoke the URL after 60s so memory frees but the new tab still renders.
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao abrir o anexo.');
    }
  };

  const handleDelete = async (attId) => {
    try {
      await api.deleteAttachment(consultationId, attId);
      const updated = attachments.filter((a) => a.attachment_id !== attId);
      setAttachments(updated);
      if (onChange) onChange(updated);
      toast.success('Anexo excluído.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao excluir o anexo.');
    } finally {
      setConfirmDeleteId(null);
    }
  };

  return (
    <div
      className={`rounded-xl border p-6 ${
        isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'
      }`}
      data-testid="attachments-panel"
    >
      <div className="flex items-start gap-3 mb-2">
        <Paperclip className="h-5 w-5 text-primary mt-1" />
        <div className="flex-1">
          <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Anexos Clínicos
          </h2>
          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            Imagens, laudos e documentos complementares
          </p>
        </div>
        <span
          title="Arquivos anexados ficam vinculados a esta consulta e são listados no PDF exportado. Máximo 10 arquivos de até 10MB cada. Formatos aceitos: JPG, PNG, PDF."
          className={`inline-flex items-center cursor-help ${
            isDark ? 'text-slate-400' : 'text-slate-500'
          }`}
          data-testid="attachments-info-tooltip"
        >
          <Info className="h-4 w-4" />
        </span>
      </div>

      {/* Upload dropzone */}
      {canUpload ? (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          onDrop={onDrop}
          onDragOver={onDragOver}
          className={`w-full mt-3 mb-4 rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
            isDark
              ? 'border-slate-600 hover:border-primary text-slate-300 bg-slate-900/40'
              : 'border-slate-300 hover:border-primary text-slate-600 bg-slate-50'
          }`}
          data-testid="attachments-dropzone"
        >
          <Upload className="h-6 w-6 mx-auto mb-2 opacity-70" />
          <div className="text-sm font-medium">
            Arraste arquivos aqui ou clique para selecionar
          </div>
          <div className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            JPG, PNG, HEIC, PDF · Máximo 10MB por arquivo
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={accept}
            onChange={(e) => handleFiles(e.target.files)}
            className="hidden"
            data-testid="attachments-file-input"
          />
        </button>
      ) : (
        <p
          className={`mt-3 mb-4 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
          data-testid="attachments-limit-reached"
        >
          Limite de 10 anexos atingido.
        </p>
      )}

      {/* In-flight uploads */}
      {pending.map((p) => (
        <div
          key={p.id}
          className={`flex items-center gap-3 p-3 rounded-lg border mb-2 ${
            isDark ? 'border-slate-700 bg-slate-900/40' : 'border-slate-200 bg-slate-50'
          }`}
          data-testid="attachment-upload-row"
        >
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <div className="flex-1 min-w-0">
            <div className={`text-sm truncate ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
              {truncateName(p.name)}
            </div>
            {p.error ? (
              <div className="text-xs text-red-500 mt-1">{p.error}</div>
            ) : (
              <div
                className={`h-1.5 mt-1 rounded ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}
              >
                <div
                  className="h-1.5 rounded bg-primary"
                  style={{ width: `${p.progress || 0}%` }}
                />
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Attachment list */}
      <ul className="space-y-2">
        {attachments.map((att) => {
          const isImage = att.file_type === 'image';
          const Icon = isImage ? ImageIcon : FileText;
          return (
            <li
              key={att.attachment_id}
              className={`flex flex-col gap-2 p-3 rounded-lg border ${
                isDark ? 'border-slate-700 bg-slate-900/40' : 'border-slate-200 bg-slate-50'
              }`}
              data-testid={`attachment-row-${att.attachment_id}`}
            >
              <div className="flex flex-wrap items-center gap-3">
                <Icon className={`h-5 w-5 ${isImage ? 'text-blue-500' : 'text-slate-500'}`} />
                <span
                  className={`flex-1 min-w-0 truncate text-sm font-medium ${
                    isDark ? 'text-slate-100' : 'text-slate-800'
                  }`}
                  title={att.original_filename}
                  data-testid="attachment-name"
                >
                  {truncateName(att.original_filename)}
                </span>
                <Badge
                  variant="secondary"
                  className={
                    isImage
                      ? 'bg-blue-100 text-blue-700 border-blue-200'
                      : 'bg-slate-200 text-slate-700 border-slate-300'
                  }
                  data-testid="attachment-type-badge"
                >
                  {isImage ? 'Imagem' : 'Documento'}
                </Badge>
                <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Enviado {relativeTime(att.uploaded_at)}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleView(att)}
                  data-testid="attachment-view-btn"
                  className="text-primary"
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Visualizar
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmDeleteId(att.attachment_id)}
                  data-testid="attachment-delete-btn"
                  className="text-red-500 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Excluir
                </Button>
              </div>

              {confirmDeleteId === att.attachment_id && (
                <div
                  className={`flex items-center justify-between gap-3 p-3 rounded border ${
                    isDark
                      ? 'border-red-900/60 bg-red-950/30 text-red-200'
                      : 'border-red-200 bg-red-50 text-red-800'
                  }`}
                  data-testid="attachment-delete-confirm"
                >
                  <span className="text-xs">
                    Excluir este anexo? Esta ação não pode ser desfeita.
                  </span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(att.attachment_id)}
                      data-testid="attachment-delete-confirm-btn"
                    >
                      Excluir
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setConfirmDeleteId(null)}
                      data-testid="attachment-delete-cancel-btn"
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
